import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { n8nApi } from '../../services/n8nApi';
import { supabase } from '../../services/supabase';

// Status possíveis para cada contato
const STATUS = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    SENT: 'sent',
    ERROR: 'error'
};

export default function SenderPage() {
    const { user } = useAuth();
    const [contacts, setContacts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showReport, setShowReport] = useState(false);
    const [report, setReport] = useState({ total: 0, success: 0, error: 0 });
    const [instanceName, setInstanceName] = useState('');

    // Carregar instância do usuário
    useEffect(() => {
        const loadUserInstance = async () => {
            try {
                const { data, error } = await supabase
                    .from('instances')
                    .select('instance_name, status')
                    .eq('user_id', user.id)
                    .maybeSingle();

                if (error) throw error;

                if (data) {
                    if (data.status !== 'connected') {
                        setError('A instância precisa estar conectada para enviar mensagens');
                        return;
                    }
                    if (!data.instance_name) {
                        setError('Nome da instância não encontrado');
                        return;
                    }
                    setInstanceName(data.instance_name);
                } else {
                    setError('Nenhuma instância encontrada. Configure uma instância primeiro.');
                }
            } catch (error) {
                console.error('Erro ao carregar instância:', error);
                setError('Erro ao carregar instância. Tente novamente.');
            }
        };

        if (user) {
            loadUserInstance();
        }
    }, [user]);

    const processCSV = (file) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const text = e.target.result;
                const lines = text.split('\n');
                const headers = lines[0].split(',').map(header => header.trim().toLowerCase());
                
                // Identificar colunas de nome
                const nameColumns = headers.reduce((acc, header, index) => {
                    if (header.includes('name') || 
                        header.includes('nome') || 
                        ['first', 'middle', 'last'].some(part => header.includes(part))) {
                        acc.push(index);
                    }
                    return acc;
                }, []);

                // Identificar colunas de telefone
                const phoneColumns = headers.reduce((acc, header, index) => {
                    if (header.includes('phone') || 
                        header.includes('telefone') || 
                        header.includes('número') || 
                        header.includes('numero') || 
                        header.includes('number') || 
                        header.includes('whatsapp')) {
                        acc.push(index);
                    }
                    return acc;
                }, []);

                // Processar linhas
                const processedContacts = lines.slice(1)
                    .map(line => {
                        if (!line.trim()) return null;
                        
                        const values = line.split(',').map(value => value.trim());
                        
                        // Processar nome
                        const names = nameColumns.map(index => values[index] || '').filter(Boolean);
                        const fullName = names.join(' ').trim();

                        // Processar números
                        const numbers = phoneColumns
                            .map(index => values[index] || '')
                            .filter(Boolean)
                            .map(number => {
                                // Remover caracteres especiais e espaços
                                const cleanNumber = number.replace(/[^0-9]/g, '');
                                
                                if (cleanNumber.length < 10) return null; // Ignorar números sem DDD

                                if (cleanNumber.startsWith('55')) {
                                    if (cleanNumber.length === 13) return cleanNumber;
                                    if (cleanNumber.length === 12) {
                                        return cleanNumber.slice(0, 4) + '9' + cleanNumber.slice(4);
                                    }
                                    return null;
                                }

                                if (cleanNumber.length === 11) return '55' + cleanNumber;
                                if (cleanNumber.length === 10) return '55' + cleanNumber.slice(0, 2) + '9' + cleanNumber.slice(2);
                                
                                return null;
                            })
                            .filter(Boolean);

                        if (!fullName || numbers.length === 0) return null;

                        return {
                            name: fullName,
                            numbers: numbers,
                            status: STATUS.PENDING
                        };
                    })
                    .filter(Boolean);

                setContacts(processedContacts);
                setError('');
            } catch (error) {
                console.error('Erro ao processar arquivo:', error);
                setError('Erro ao processar arquivo. Verifique o formato e tente novamente.');
            }
        };

        reader.onerror = () => {
            setError('Erro ao ler arquivo');
        };

        reader.readAsText(file);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setIsLoading(true);
            processCSV(file);
            setIsLoading(false);
        }
    };

    const handleStartSending = async () => {
        if (!instanceName) {
            setError('Nenhuma instância configurada');
            return;
        }

        if (contacts.length === 0) {
            setError('Nenhum contato para enviar');
            return;
        }

        setIsLoading(true);
        let successCount = 0;
        let errorCount = 0;

        // Copiar contatos para não modificar o estado diretamente
        const updatedContacts = [...contacts];

        // Enviar um contato por vez
        for (let i = 0; i < updatedContacts.length; i++) {
            const contact = updatedContacts[i];
            
            // Atualizar status para processando
            contact.status = STATUS.PROCESSING;
            setContacts([...updatedContacts]);

            try {
                // Enviar para o webhook do n8n
                const response = await n8nApi.sendMessage({
                    instanceName: instanceName,
                    name: contact.name,
                    phone: contact.numbers[0]
                });

                // Atualizar status baseado na resposta
                if (response.success) {
                    contact.status = STATUS.SENT;
                    successCount++;
                } else {
                    contact.status = STATUS.ERROR;
                    errorCount++;
                }
            } catch (error) {
                console.error('Erro ao enviar mensagem:', error);
                contact.status = STATUS.ERROR;
                errorCount++;
            }

            setContacts([...updatedContacts]);
        }

        // Mostrar relatório
        setReport({
            total: contacts.length,
            success: successCount,
            error: errorCount
        });
        setShowReport(true);
        setIsLoading(false);
    };

    const handleClearList = () => {
        setContacts([]);
        setError('');
        setShowReport(false);
    };

    const handleRemoveContact = (index) => {
        const newContacts = contacts.filter((_, i) => i !== index);
        setContacts(newContacts);
    };

    return (
        <div className="space-y-6">
            {/* Input de arquivo CSV */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Importar Contatos (CSV)
                </label>
                <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => processCSV(e.target.files[0])}
                    className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-medium
                        file:bg-primary file:text-white
                        hover:file:bg-primary-dark
                        file:cursor-pointer"
                />
            </div>

            {/* Lista de Contatos */}
            {contacts.length > 0 && (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">
                            Contatos ({contacts.length})
                        </h3>
                        <button
                            onClick={handleClearList}
                            className="text-sm text-accent hover:text-accent-dark"
                        >
                            Limpar Lista
                        </button>
                    </div>
                    
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900">Nome</th>
                                    <th className="px-3 py-3.5 text-left text-sm font-medium text-gray-900">Telefone</th>
                                    <th className="px-3 py-3.5 text-left text-sm font-medium text-gray-900">Status</th>
                                    <th className="px-3 py-3.5 text-left text-sm font-medium text-gray-900">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {contacts.map((contact, index) => (
                                    <tr key={index}>
                                        <td className="py-4 pl-4 pr-3 text-sm text-gray-900">{contact.name}</td>
                                        <td className="px-3 py-4 text-sm text-gray-500">{contact.numbers[0]}</td>
                                        <td className="px-3 py-4 text-sm">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                                ${contact.status === STATUS.SENT ? 'bg-success/10 text-success' :
                                                  contact.status === STATUS.ERROR ? 'bg-error/10 text-error' :
                                                  contact.status === STATUS.PROCESSING ? 'bg-primary/10 text-primary' :
                                                  'bg-gray-100 text-gray-800'}`}>
                                                {contact.status === STATUS.SENT ? 'Enviado' :
                                                 contact.status === STATUS.ERROR ? 'Erro' :
                                                 contact.status === STATUS.PROCESSING ? 'Enviando' :
                                                 'Pendente'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-4 text-sm">
                                            <button
                                                onClick={() => handleRemoveContact(index)}
                                                disabled={contact.status === STATUS.PROCESSING}
                                                className="text-error hover:text-error/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title={contact.status === STATUS.PROCESSING ? 'Não é possível excluir durante o envio' : 'Excluir contato'}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Botão de Envio */}
            {contacts.length > 0 && !isLoading && !showReport && (
                <div className="flex justify-end">
                    <button
                        onClick={handleStartSending}
                        disabled={isLoading}
                        className="px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Iniciar Envio
                    </button>
                </div>
            )}

            {/* Loading */}
            {isLoading && (
                <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">Enviando mensagens...</p>
                </div>
            )}

            {/* Mensagem de Erro */}
            {error && (
                <div className="p-4 bg-error/10 text-error rounded-md">
                    {error}
                </div>
            )}

            {/* Relatório */}
            {showReport && (
                <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Relatório de Envio</h3>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white p-4 rounded-md shadow-sm">
                            <p className="text-sm text-gray-500">Total</p>
                            <p className="text-2xl font-semibold text-gray-900">{report.total}</p>
                        </div>
                        <div className="bg-white p-4 rounded-md shadow-sm">
                            <p className="text-sm text-gray-500">Enviados</p>
                            <p className="text-2xl font-semibold text-success">{report.success}</p>
                        </div>
                        <div className="bg-white p-4 rounded-md shadow-sm">
                            <p className="text-sm text-gray-500">Erros</p>
                            <p className="text-2xl font-semibold text-error">{report.error}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
